/**
 * Lawyer Remittance Import — Phase 2 wizard UI
 *
 * Three-step state machine:
 *   1. setup     — pick lawyer + upload CSV → POST /lawyer-remittance/preview
 *   2. resolve   — per-row resolution table + running totals
 *   3. results   — per-row pass/fail; failed rows remain editable + retry
 *
 * Match-state legend (kept in `MATCH_DECOR` below):
 *   🟢 matched   — auto-picked deal, single confident match
 *   🟡 ambiguous — 2+ candidates ≥ fuzzy threshold; operator picks one
 *   ⚪ grouped   — semicolon-split multi-merchant Case Link; operator picks one
 *   🔴 unmatched — no candidate above threshold; operator manually searches
 *
 * Constraints (per spec):
 *   - No new backend endpoints; this page only uses /api/lawyer-remittance/preview
 *     and /commit and the existing /contacts and /deals/search endpoints.
 *   - Every row REQUIRES an explicit operator click before commit; no auto-commit.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import {
  Upload, FileSpreadsheet, AlertTriangle, CheckCircle, XCircle,
  Loader2, ArrowRight, ArrowLeft, Search, RotateCcw, MinusCircle, Scale, Receipt,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

const MATCH_DECOR = {
  matched:   { dot: 'bg-green-500',  pill: 'bg-green-50 text-green-700 border-green-200',  label: 'Auto-match'  },
  ambiguous: { dot: 'bg-amber-500',  pill: 'bg-amber-50 text-amber-700 border-amber-200',  label: 'Pick one'    },
  grouped:   { dot: 'bg-slate-400',  pill: 'bg-slate-50 text-slate-700 border-slate-200',  label: 'Multi-merchant' },
  unmatched: { dot: 'bg-red-500',    pill: 'bg-red-50 text-red-700 border-red-200',        label: 'Find deal'   },
  skipped:   { dot: 'bg-slate-300',  pill: 'bg-slate-100 text-slate-500 border-slate-200', label: 'Skipped'     },
  resolved:  { dot: 'bg-green-600',  pill: 'bg-green-100 text-green-800 border-green-300', label: 'Resolved'    },
};

/** Derive a logical UI state for a row given the matcher output + operator action. */
function deriveRowUiState(row) {
  if (row.skipped) return 'skipped';
  if (row.chosen_deal_id) return 'resolved';
  // Multi-merchant (grouped) downgrade: even if matcher auto-picked, semicolon-split
  // names need explicit operator confirmation — surface as 'grouped' until picked.
  if ((row.merchant_names?.length ?? 1) > 1 && row.match.status === 'matched') {
    return 'grouped';
  }
  return row.match.status;
}

// `embedded` (09/28): rendered as a tab inside pages/Legal.js — no Layout, no page title.
const LawyerRemittanceImport = ({ embedded = false }) => {
  const { getAuthHeaders } = useAuth();
  const headers = useMemo(() => getAuthHeaders(), [getAuthHeaders]);

  // ─── step state ────────────────────────────────────────────────────────────
  const [step, setStep] = useState('setup'); // 'setup' | 'resolve' | 'results'

  // ─── setup state ───────────────────────────────────────────────────────────
  const [lawyers, setLawyers] = useState([]);
  const [lawyerId, setLawyerId] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const inputRef = useRef(null);

  // ─── preview / resolve state ───────────────────────────────────────────────
  const [preview, setPreview] = useState(null);
  // rows: array of { key, kind, ...parsed, match, chosen_deal_id, chosen_label, skipped, warnings }
  const [rows, setRows] = useState([]);

  // ─── results state ─────────────────────────────────────────────────────────
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);

  // ── Load lawyer contacts on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/contacts?contact_type=lawyer&limit=500`, { headers });
        const list = Array.isArray(r.data) ? r.data : (r.data?.contacts || []);
        setLawyers(list.filter(c => c.is_lawyer));
      } catch (e) {
        toast.error('Failed to load lawyer contacts');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── File handling ─────────────────────────────────────────────────────────
  const acceptFile = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error('Please choose a .csv file');
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  // ─── Preview ───────────────────────────────────────────────────────────────
  const runPreview = async () => {
    if (!lawyerId || !file) return;
    setLoadingPreview(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('lawyer_contact_id', lawyerId);
      const r = await axios.post(`${API}/api/lawyer-remittance/preview`, form, { headers });
      setPreview(r.data);

      // Materialize rows (collections then invoices) with operator-mutable fields.
      const built = [
        ...r.data.collections.map(c => ({
          key: `c-${c.row_index}`,
          kind: 'collection',
          ...c,
          chosen_deal_id: c.match.status === 'matched' && (c.merchant_names?.length ?? 1) === 1
            ? c.match.chosen?.deal_id
            : null,
          chosen_label: c.match.status === 'matched' && (c.merchant_names?.length ?? 1) === 1
            ? `#${c.match.chosen?.deal_id_number} — ${c.match.chosen?.company_name ?? ''}`
            : null,
          skipped: false,
        })),
        ...r.data.invoices.map(c => ({
          key: `i-${c.row_index}`,
          kind: 'invoice',
          ...c,
          chosen_deal_id: c.match.status === 'matched' && (c.merchant_names?.length ?? 1) === 1
            ? c.match.chosen?.deal_id
            : null,
          chosen_label: c.match.status === 'matched' && (c.merchant_names?.length ?? 1) === 1
            ? `#${c.match.chosen?.deal_id_number} — ${c.match.chosen?.company_name ?? ''}`
            : null,
          skipped: false,
        })),
      ];
      setRows(built);
      setStep('resolve');
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message
          ? `${detail.message}: ${(detail.errors || []).slice(0,3).map(x => x.message).join('; ')}`
          : 'Preview failed';
      toast.error(msg);
    } finally {
      setLoadingPreview(false);
    }
  };

  // ─── Row mutators ──────────────────────────────────────────────────────────
  const pickCandidate = (key, candidate) => {
    setRows(rs => rs.map(r => r.key === key
      ? { ...r, chosen_deal_id: candidate.deal_id, chosen_label: `#${candidate.deal_id_number} — ${candidate.company_name ?? ''}`, skipped: false }
      : r));
  };

  const clearPick = (key) => {
    setRows(rs => rs.map(r => r.key === key ? { ...r, chosen_deal_id: null, chosen_label: null } : r));
  };

  const toggleSkip = (key) => {
    setRows(rs => rs.map(r => r.key === key
      ? { ...r, skipped: !r.skipped, chosen_deal_id: !r.skipped ? null : r.chosen_deal_id, chosen_label: !r.skipped ? null : r.chosen_label }
      : r));
  };

  // ─── Totals ────────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    const collections = rows.filter(r => r.kind === 'collection' && !r.skipped);
    const invoices = rows.filter(r => r.kind === 'invoice' && !r.skipped);
    const resolved = rows.filter(r => !!r.chosen_deal_id || r.skipped).length;
    return {
      total: rows.length,
      resolved,
      pending: rows.length - resolved,
      gross: collections.reduce((s, r) => s + (r.gross || 0), 0),
      fee: collections.reduce((s, r) => s + (r.lawyer_fee || 0), 0),
      net: collections.reduce((s, r) => s + (r.net_to_pfs || 0), 0),
      invoice_total: invoices.reduce((s, r) => s + (r.amount || 0), 0),
    };
  }, [rows]);

  const csvTotals = preview?.totals;
  const grossDelta = csvTotals ? totals.gross - csvTotals.gross : 0;

  // ─── Commit ────────────────────────────────────────────────────────────────
  const canPost = totals.pending === 0 && rows.some(r => !!r.chosen_deal_id);
  const buildCommitPayload = () => {
    const eligible = rows.filter(r => !r.skipped && r.chosen_deal_id);
    return {
      lawyer_contact_id: lawyerId,
      collections: eligible.filter(r => r.kind === 'collection').map(r => ({
        row_index: r.row_index,
        deal_id: r.chosen_deal_id,
        date: r.date,
        gross_collected: r.gross,
        lawyer_fee: r.lawyer_fee,
        remit_reference: `LRI-${preview?.lawyer?.id?.slice(0,6) || 'X'}-${r.row_index}`,
        notes: `Lawyer remittance: ${r.merchant_text}`,
        merchant_text: r.merchant_text,
      })),
      invoices: eligible.filter(r => r.kind === 'invoice').map(r => ({
        row_index: r.row_index,
        deal_id: r.chosen_deal_id,
        date: r.date,
        amount: r.amount,
        description: r.invoice_number || `Lawyer invoice — ${r.merchant_text}`,
        invoice_reference: r.invoice_number,
      })),
    };
  };

  const commit = async () => {
    if (!canPost) return;
    setCommitting(true);
    try {
      const payload = buildCommitPayload();
      const r = await axios.post(`${API}/api/lawyer-remittance/commit`, payload, { headers });
      setCommitResult(r.data);
      setStep('results');
      if (r.data.failed_count === 0) {
        toast.success(`Posted ${r.data.succeeded_count} entries from ${preview?.lawyer?.company_name}`);
      } else {
        toast.warning(`Posted ${r.data.succeeded_count}; ${r.data.failed_count} failed — review below`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  };

  // ─── Retry from results ────────────────────────────────────────────────────
  const retryFailedRows = () => {
    if (!commitResult) return;
    // Mark successfully-posted rows as skipped so the operator only retries failures.
    const failedIdx = new Set([
      ...(commitResult.collection_results || []).filter(r => r.status === 'failed').map(r => `c-${r.row_index}`),
      ...(commitResult.invoice_results || []).filter(r => r.status === 'failed').map(r => `i-${r.row_index}`),
    ]);
    setRows(rs => rs.map(r => failedIdx.has(r.key)
      ? r // keep editable
      : { ...r, skipped: true }));
    setStep('resolve');
    setCommitResult(null);
  };

  const resetAll = () => {
    setStep('setup');
    setPreview(null);
    setRows([]);
    setFile(null);
    setCommitResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  // ────────────────────────────────────────────────────────────────────────────
  const Wrapper = embedded ? React.Fragment : Layout;
  return (
    <Wrapper>
      <div className={embedded ? 'space-y-6' : 'space-y-6 p-6 max-w-7xl mx-auto'} data-testid="lawyer-remittance-page">
        <div className="flex items-center justify-between">
          <div>
            {!embedded && (
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <Scale className="h-6 w-6 text-indigo-600" />
                Lawyer Remittance Import
              </h1>
            )}
            <p className="text-sm text-slate-500 mt-1">
              Bulk-post lawyer collections and invoices from a monthly remittance CSV.
              Every row requires explicit operator confirmation before posting.
            </p>
          </div>
          <Stepper step={step} />
        </div>

        {step === 'setup' && (
          <SetupStep
            lawyers={lawyers}
            lawyerId={lawyerId}
            setLawyerId={setLawyerId}
            file={file}
            setFile={acceptFile}
            inputRef={inputRef}
            dragOver={dragOver}
            setDragOver={setDragOver}
            onDrop={onDrop}
            onContinue={runPreview}
            loading={loadingPreview}
          />
        )}

        {step === 'resolve' && preview && (
          <ResolveStep
            preview={preview}
            rows={rows}
            totals={totals}
            csvTotals={csvTotals}
            grossDelta={grossDelta}
            onPickCandidate={pickCandidate}
            onClearPick={clearPick}
            onToggleSkip={toggleSkip}
            onBack={resetAll}
            onPostAll={commit}
            canPost={canPost}
            committing={committing}
            headers={headers}
          />
        )}

        {step === 'results' && commitResult && (
          <ResultsStep
            result={commitResult}
            onRetryFailed={retryFailedRows}
            onDone={resetAll}
            hasFailures={commitResult.failed_count > 0}
          />
        )}
      </div>
    </Wrapper>
  );
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const Stepper = ({ step }) => {
  const stepIdx = { setup: 0, resolve: 1, results: 2 }[step] ?? 0;
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="lri-stepper">
      {['Upload', 'Resolve', 'Results'].map((label, i) => (
        <div key={label} className="flex items-center gap-1.5">
          <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
            i < stepIdx ? 'bg-green-500 text-white'
            : i === stepIdx ? 'bg-indigo-600 text-white'
            : 'bg-slate-200 text-slate-500'
          }`}>
            {i < stepIdx ? '✓' : i + 1}
          </div>
          <span className={i <= stepIdx ? 'text-slate-700 font-medium' : 'text-slate-400'}>{label}</span>
          {i < 2 && <ArrowRight className="h-3 w-3 text-slate-300" />}
        </div>
      ))}
    </div>
  );
};

const SetupStep = ({ lawyers, lawyerId, setLawyerId, file, setFile, inputRef, dragOver, setDragOver, onDrop, onContinue, loading }) => {
  const canContinue = !!lawyerId && !!file && !loading;
  return (
    <Card data-testid="lri-setup-card">
      <CardHeader>
        <CardTitle>Step 1 · Upload remittance CSV</CardTitle>
        <CardDescription>
          Choose the lawyer firm whose CSV you're importing and pick the file. The
          lawyer dropdown is used to score deals with active legal cases higher
          during matching.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <Label htmlFor="lri-lawyer-select">Lawyer firm</Label>
          <Select value={lawyerId} onValueChange={setLawyerId}>
            <SelectTrigger className="w-full mt-1.5" id="lri-lawyer-select" data-testid="lri-lawyer-select">
              <SelectValue placeholder={lawyers.length ? 'Pick a lawyer firm…' : 'Loading lawyers…'} />
            </SelectTrigger>
            <SelectContent>
              {lawyers.map(l => (
                <SelectItem key={l.id} value={l.id} data-testid={`lri-lawyer-option-${l.id}`}>
                  {l.company_name}
                  {((l.is_default_for || []).includes('lawyer') || l.is_default_lawyer) && <Badge variant="outline" className="ml-2 text-[10px]">Default</Badge>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Remittance CSV file</Label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`mt-1.5 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            data-testid="lri-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0])}
              data-testid="lri-file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-3 text-slate-700">
                <FileSpreadsheet className="h-6 w-6 text-green-600" />
                <div className="text-left">
                  <div className="font-medium" data-testid="lri-file-name">{file.name}</div>
                  <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
                </div>
              </div>
            ) : (
              <div className="text-slate-500">
                <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                <div className="font-medium">Drop CSV here or click to browse</div>
                <div className="text-xs mt-1">Sample column layout: Contact, Date, Related To, Case Link, Debit, Credit, Sum of Remittance, Collections Fee</div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={onContinue} disabled={!canContinue} data-testid="lri-continue-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Continue <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ResolveStep = ({
  preview, rows, totals, csvTotals, grossDelta,
  onPickCandidate, onClearPick, onToggleSkip,
  onBack, onPostAll, canPost, committing, headers,
}) => {
  return (
    <>
      <Card data-testid="lri-resolve-header">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Step 2 · Resolve {totals.total} parsed entries</CardTitle>
              <CardDescription>
                {preview.lawyer?.company_name} ·
                {' '}
                <span className="font-medium text-indigo-700">{totals.resolved}/{totals.total}</span>
                {' '}rows resolved · default contingency {preview.lawyer?.legal_fee_pct ?? preview.lawyer?.default_contingency_rate ?? '—'}%
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onBack} data-testid="lri-resolve-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Cancel
              </Button>
            </div>
          </div>
          <div className="flex gap-2 mt-3 flex-wrap text-xs">
            {Object.entries(MATCH_DECOR).filter(([k]) => !['resolved', 'skipped'].includes(k)).map(([k, v]) => (
              <span key={k} className={`px-2 py-0.5 rounded-full border ${v.pill} flex items-center gap-1.5`}>
                <span className={`h-2 w-2 rounded-full ${v.dot}`} /> {v.label}
              </span>
            ))}
          </div>
        </CardHeader>
      </Card>

      <div className="border rounded-lg overflow-hidden bg-white" data-testid="lri-resolve-table">
        <div className="grid grid-cols-[40px_60px_60px_90px_1fr_120px_120px_1.4fr_56px] gap-2 px-3 py-2 bg-slate-50 border-b text-xs font-medium text-slate-600 uppercase">
          <div></div>
          <div>Row</div>
          <div>Type</div>
          <div>Date</div>
          <div>Merchant (Case Link)</div>
          <div className="text-right">Gross</div>
          <div className="text-right">Fee / Net</div>
          <div>Deal</div>
          <div className="text-right">Skip</div>
        </div>

        {rows.map(row => (
          <RowEditor
            key={row.key}
            row={row}
            onPickCandidate={onPickCandidate}
            onClearPick={onClearPick}
            onToggleSkip={onToggleSkip}
            headers={headers}
          />
        ))}
      </div>

      <Card data-testid="lri-totals-footer">
        <CardContent className="py-4 grid grid-cols-1 md:grid-cols-5 gap-4">
          <Stat label="Resolved" value={`${totals.resolved} / ${totals.total}`}
            highlight={totals.pending === 0 ? 'green' : 'amber'} dataTestid="lri-stat-resolved" />
          <Stat label="Collections Gross" value={formatCurrency(totals.gross)} dataTestid="lri-stat-gross" />
          <Stat label="Lawyer Fees" value={formatCurrency(totals.fee)} dataTestid="lri-stat-fee" />
          <Stat label="Net to PFS" value={formatCurrency(totals.net)} dataTestid="lri-stat-net" />
          <Stat label="Invoices" value={formatCurrency(totals.invoice_total)} dataTestid="lri-stat-invoices" />
        </CardContent>
        {csvTotals && Math.abs(grossDelta) > 0.05 && (
          <CardContent className="pt-0 pb-4">
            <Alert className="border-amber-300 bg-amber-50" data-testid="lri-totals-mismatch">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-xs">
                CSV footer reports gross {formatCurrency(csvTotals.gross)} — your selection
                covers {formatCurrency(totals.gross)} ({grossDelta > 0 ? '+' : ''}{formatCurrency(grossDelta)}).
                This is expected if you skipped or excluded rows.
              </AlertDescription>
            </Alert>
          </CardContent>
        )}
        <CardContent className="pt-0 flex items-center justify-between border-t">
          <div className="text-xs text-slate-500">
            {totals.pending > 0
              ? `${totals.pending} row${totals.pending === 1 ? '' : 's'} still need attention before posting.`
              : 'All rows resolved or skipped — ready to post.'}
          </div>
          <Button
            onClick={onPostAll}
            disabled={!canPost || committing}
            data-testid="lri-post-all-btn"
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {committing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Post All Resolved Entries
          </Button>
        </CardContent>
      </Card>
    </>
  );
};

const Stat = ({ label, value, highlight, dataTestid }) => {
  const cls = highlight === 'green' ? 'text-green-700'
    : highlight === 'amber' ? 'text-amber-700'
    : 'text-slate-800';
  return (
    <div data-testid={dataTestid}>
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${cls}`}>{value}</div>
    </div>
  );
};

const RowEditor = ({ row, onPickCandidate, onClearPick, onToggleSkip, headers }) => {
  const uiState = deriveRowUiState(row);
  const decor = MATCH_DECOR[uiState] ?? MATCH_DECOR.unmatched;
  const hasWarning = (row.warnings || []).length > 0;
  const isCollection = row.kind === 'collection';

  return (
    <div
      className={`grid grid-cols-[40px_60px_60px_90px_1fr_120px_120px_1.4fr_56px] gap-2 px-3 py-3 border-b items-center text-sm ${
        row.skipped ? 'bg-slate-50 text-slate-400' : 'hover:bg-slate-50/50'
      }`}
      data-testid={`lri-row-${row.key}`}
      data-row-state={uiState}
    >
      <div className="flex items-center justify-center">
        <span className={`h-3 w-3 rounded-full ${decor.dot}`} title={decor.label} data-testid={`lri-row-status-${row.key}`} />
      </div>
      <div className="text-slate-500 text-xs font-mono">{row.row_index}</div>
      <div>
        {isCollection
          ? <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Coll</Badge>
          : <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200"><Receipt className="h-2.5 w-2.5 mr-0.5 inline" />Inv</Badge>}
      </div>
      <div className="text-xs">{row.date}</div>
      <div className="min-w-0">
        <div className="font-medium truncate" title={row.merchant_text}>{row.merchant_text}</div>
        {(row.merchant_names?.length ?? 1) > 1 && (
          <div className="text-[10px] text-slate-500 mt-0.5">⚪ {row.merchant_names.length} merchants bundled</div>
        )}
        {hasWarning && (
          <div className="flex items-start gap-1 mt-0.5">
            <AlertTriangle className="h-3 w-3 text-amber-600 mt-0.5 flex-shrink-0" data-testid={`lri-warning-${row.key}`} />
            <div className="text-[10px] text-amber-700">{row.warnings[0]}</div>
          </div>
        )}
      </div>
      <div className="text-right">
        {isCollection
          ? <span className="font-mono text-sm">{formatCurrency(row.gross)}</span>
          : <span className="font-mono text-sm">{formatCurrency(row.amount)}</span>}
      </div>
      <div className="text-right">
        {isCollection ? (
          <>
            <div className="font-mono text-xs text-amber-700">{formatCurrency(row.lawyer_fee)}</div>
            <div className="font-mono text-[10px] text-slate-500">→ {formatCurrency(row.net_to_pfs)}</div>
            {row.implied_fee_pct != null && (
              <div className="text-[10px] text-slate-400">{row.implied_fee_pct}%</div>
            )}
          </>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </div>
      <div className="min-w-0">
        <DealPicker row={row} headers={headers} onPickCandidate={onPickCandidate} onClearPick={onClearPick} />
      </div>
      <div className="text-right">
        <Button
          variant="ghost"
          size="icon"
          className={row.skipped ? 'text-amber-600' : 'text-slate-400 hover:text-red-600'}
          onClick={() => onToggleSkip(row.key)}
          title={row.skipped ? 'Un-skip' : 'Skip this row'}
          data-testid={`lri-skip-btn-${row.key}`}
        >
          {row.skipped ? <RotateCcw className="h-4 w-4" /> : <MinusCircle className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

const DealPicker = ({ row, headers, onPickCandidate, onClearPick }) => {
  const [open, setOpen] = useState(false);
  const candidates = row.match?.candidates || [];

  if (row.skipped) {
    return <span className="text-xs italic text-slate-400">Skipped</span>;
  }
  if (row.chosen_deal_id) {
    return (
      <div className="flex items-center gap-1">
        <Badge className="bg-green-100 text-green-800 border-green-300" data-testid={`lri-chosen-${row.key}`}>
          <CheckCircle className="h-3 w-3 mr-1" />{row.chosen_label}
        </Badge>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onClearPick(row.key)} title="Change">
          <XCircle className="h-3 w-3 text-slate-400" />
        </Button>
      </div>
    );
  }
  // candidate pickers: ambiguous + grouped + matched-but-multi → show inline buttons
  if (candidates.length > 0) {
    return (
      <div className="flex flex-wrap gap-1" data-testid={`lri-candidates-${row.key}`}>
        {candidates.slice(0, 3).map(c => (
          <button
            key={c.deal_id}
            onClick={() => onPickCandidate(row.key, c)}
            className="px-1.5 py-0.5 rounded border border-slate-300 hover:border-indigo-500 hover:bg-indigo-50 text-xs font-medium flex items-center gap-1"
            data-testid={`lri-candidate-btn-${row.key}-${c.deal_id_number}`}
            title={c.match_reason}
          >
            #{c.deal_id_number}
            {c.has_active_legal_case_with_lawyer && <span title="Has active legal case w/ this lawyer">⚖️</span>}
            {c.is_defaulted && <span title="Defaulted" className="text-red-500">●</span>}
          </button>
        ))}
        {candidates.length > 3 && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button className="px-1.5 py-0.5 rounded border border-slate-300 text-xs">+{candidates.length - 3} more</button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-1">
              {candidates.slice(3).map(c => (
                <button
                  key={c.deal_id}
                  onClick={() => { onPickCandidate(row.key, c); setOpen(false); }}
                  className="w-full text-left px-2 py-1 hover:bg-slate-100 rounded text-xs"
                >
                  #{c.deal_id_number} — {c.company_name} ({c.deal_status})
                </button>
              ))}
            </PopoverContent>
          </Popover>
        )}
        <ManualSearch row={row} headers={headers} onPick={onPickCandidate} />
      </div>
    );
  }
  // No candidates at all → manual search only
  return <ManualSearch row={row} headers={headers} onPick={onPickCandidate} />;
};

const ManualSearch = ({ row, headers, onPick }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const tRef = useRef();

  const runSearch = useCallback((term) => {
    if (!term || term.length < 2) { setResults([]); return; }
    setSearching(true);
    axios.get(`${API}/api/deals?search=${encodeURIComponent(term)}&limit=10`, { headers })
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.deals || []);
        setResults(list);
      })
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [headers]);

  useEffect(() => {
    clearTimeout(tRef.current);
    tRef.current = setTimeout(() => runSearch(q), 250);
    return () => clearTimeout(tRef.current);
  }, [q, runSearch]);

  // Seed query with first merchant name when opening
  const onOpen = (v) => {
    setOpen(v);
    if (v && !q && row.merchant_names?.[0]) {
      setQ(row.merchant_names[0]);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button
          className="px-1.5 py-0.5 rounded border border-dashed border-red-300 text-red-600 hover:bg-red-50 text-xs flex items-center gap-1"
          data-testid={`lri-manual-search-btn-${row.key}`}
        >
          <Search className="h-3 w-3" /> Find deal…
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" data-testid={`lri-manual-search-popover-${row.key}`}>
        <Input
          placeholder="Search by deal # or merchant name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
          className="text-sm"
          data-testid={`lri-manual-search-input-${row.key}`}
        />
        <div className="mt-2 max-h-60 overflow-y-auto">
          {searching && <div className="text-xs text-slate-400 py-2 text-center">Searching…</div>}
          {!searching && results.length === 0 && q.length >= 2 && (
            <div className="text-xs text-slate-400 py-2 text-center">No deals found</div>
          )}
          {results.map(d => (
            <button
              key={d.id}
              onClick={() => {
                onPick(row.key, {
                  deal_id: d.id,
                  deal_id_number: d.deal_id || d.id,
                  company_name: d.merchant_company_name || d.company_name || '',
                });
                setOpen(false);
              }}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-indigo-50 text-xs border-b last:border-b-0"
              data-testid={`lri-manual-search-result-${row.key}-${d.id}`}
            >
              <div className="font-medium">
                #{d.deal_id} — {d.merchant_company_name || d.company_name || '—'}
              </div>
              <div className="text-[10px] text-slate-500">{d.status} · bal {formatCurrency(d.balance || 0)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ResultsStep = ({ result, onRetryFailed, onDone, hasFailures }) => {
  const all = [
    ...(result.collection_results || []).map(r => ({ kind: 'collection', ...r })),
    ...(result.invoice_results || []).map(r => ({ kind: 'invoice', ...r })),
  ];
  return (
    <Card data-testid="lri-results-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {hasFailures
            ? <AlertTriangle className="h-5 w-5 text-amber-600" />
            : <CheckCircle className="h-5 w-5 text-green-600" />}
          Step 3 · {result.succeeded_count} posted{hasFailures ? `, ${result.failed_count} failed` : ''}
        </CardTitle>
        <CardDescription>
          {hasFailures
            ? 'Failed rows remain editable — click Retry to go back to the resolution view with only the failing rows enabled.'
            : 'All resolved entries posted successfully.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="border rounded overflow-hidden">
          {all.map((r, i) => (
            <div
              key={`${r.kind}-${r.row_index}-${i}`}
              className="grid grid-cols-[40px_80px_80px_1fr_2fr] gap-2 px-3 py-2 border-b last:border-b-0 text-xs items-center"
              data-testid={`lri-result-row-${r.kind}-${r.row_index}`}
            >
              <span className={`h-2.5 w-2.5 rounded-full ${r.status === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="font-mono text-[11px]">row {r.row_index}</span>
              <Badge variant="outline" className="text-[10px] w-fit">{r.kind === 'collection' ? 'Coll' : 'Inv'}</Badge>
              <span className="font-mono text-xs">{formatCurrency(r.gross_collected ?? r.amount ?? 0)}</span>
              <span className={r.status === 'success' ? 'text-green-700' : 'text-red-700'}>
                {r.status === 'success'
                  ? `✓ txn ${(r.transaction_id || r.id || '').slice(0,8)}…`
                  : `✗ ${typeof r.error === 'string' ? r.error : (r.error?.message || r.error?.detail || `HTTP ${r.error_status}`)}`}
              </span>
            </div>
          ))}
        </div>
        <div className="flex justify-between pt-2">
          <Button variant="outline" onClick={onDone} data-testid="lri-results-done-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Done — Upload another
          </Button>
          {hasFailures && (
            <Button onClick={onRetryFailed} data-testid="lri-results-retry-btn" className="bg-indigo-600 hover:bg-indigo-700">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Retry {result.failed_count} failed row{result.failed_count === 1 ? '' : 's'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LawyerRemittanceImport;
